// /client/src/components/AuthForm.js
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import sanitizeHtml from "sanitize-html";
import "./authform.css";
import {
  FaEye, FaEyeSlash, FaBars, FaTimes, FaPlay, FaConciergeBell, FaBrain,
  FaEnvelope, FaLock, FaUserCircle, FaCalendarAlt, FaBolt,
  FaCheckCircle, FaExclamationCircle, FaExclamationTriangle
} from "react-icons/fa";
import "./AdminDashboard/AdminDashboard.css";
import axios from 'axios'; 
import confetti from 'canvas-confetti';
import { Helmet } from 'react-helmet-async';

// Password strength regex: at least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
// Rationale: the old pattern only allowed 7 specific symbols in the *whole* password,
// so "Summer#2026" or "My_pass-word1A" were rejected. Now "special" means any
// non-alphanumeric, non-whitespace character. Upper limit of 72 matches bcrypt's
// input limit (72 bytes) so long passwords are never silently truncated.
// MUST stay identical to PASSWORD_REGEX in /server/routes/userRoutes.js.
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])\S{8,72}$/;

// Email regex for basic validation
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const usernameRegex = /^[a-zA-Z0-9_]{3,50}$/;

   const motivationalQuotes = [
    { subject: "Actuarial Science", quote: "The work of science is to substitute facts for appearances and demonstrations for impressions.", author: "John Ruskin" },
    { subject: "Aerospace Engineering", quote: "Scientists study the world as it is; engineers create the world that has never been.", author: "Theodore von Kármán" },
    { subject: "Anthropology", quote: "Archaeology is the anthropology of the past, and science fiction is the anthropology of the future.", author: "Joan D. Vinge" },
    { subject: "Archaeology", quote: "Every archaeologist knows in his heart why he digs: in pity and humility, that the dead may live again.", author: "Grahame Clark" },
    { subject: "Architecture", quote: "Architecture should speak of its time and place, but yearn for timelessness.", author: "Frank Gehry" },
{ 
    subject: "Biological Sciences", 
    quote: "A living cell is a marvel of detailed and complex architecture.", 
    author: "Carl Sagan" 
},
    { subject: "Biomedical Sciences", quote: "Everything is theoretically impossible, until it is done.", author: "Robert A. Heinlein" },
    { subject: "Business Analytics", quote: "Data is not information. Information is not knowledge. Knowledge is not understanding. Understanding is not wisdom.", author: "Clifford Stoll" },
    { subject: "Business Studies", quote: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { subject: "Chemical Engineering", quote: "Engineering is the closest thing to magic that exists in the world.", author: "Elon Musk" },
    { subject: "Chemistry", quote: "Chemistry is the science of matter and the changes it undergoes — the study of transformation itself.", author: "Marie Curie" },
    { subject: "Civil Engineering", quote: "Civil engineering is the art of directing the great sources of power in nature for the use and convenience of man.", author: "Thomas Tredgold" },
    { subject: "Classics", quote: "The man who knows and dwells in history adds a new dimension to his existence.", author: "Flinders Petrie" },
    { subject: "Computer Science", quote: "Everybody should learn to program a computer, because it teaches you how to think.", author: "Steve Jobs" },
    { subject: "Criminology", quote: "Every society gets the kind of criminal it deserves.", author: "Robert Kennedy" },
    { subject: "Dentistry", quote: "You treat a person, I guarantee you win—no matter the outcome.", author: "Patch Adams" },
    { subject: "Design", quote: "Design is thinking made visual.", author: "Saul Bass" },
    { subject: "Drama & Theatre Studies", quote: "The mission of the theatre is to change, to raise the consciousness of people to their human possibilities.", author: "Arthur Miller" },
    { subject: "Earth Sciences", quote: "We must care for the whole range of biodiversity rather than just one or two stars.", author: "Sir David Attenborough" },
    { subject: "Economics", quote: "Economics is the science which studies human behaviour as a relationship between ends and scarce means.", author: "Lionel Robbins" },
    { subject: "Education", quote: "Education is the most powerful weapon which you can use to change the world.", author: "Nelson Mandela" },
    { subject: "Electrical & Electronic Engineering", quote: "The progressive development of man is vitally dependent on invention.", author: "Nikola Tesla" },
    { subject: "Environmental Science", quote: "We won’t have a society if we destroy the environment.", author: "Margaret Mead" },
    { subject: "Fashion Design & Textiles", quote: "Fashion is the armor to survive the reality of everyday life.", author: "Bill Cunningham" },
    { subject: "Finance and Accounting", quote: "Success is the sum of small efforts, repeated day in and day out.", author: "Robert Collier" },
    { subject: "Fine Arts", quote: "Every child is an artist. The problem is how to remain an artist once he grows up.", author: "Pablo Picasso" },
    { subject: "General Engineering", quote: "Scientists dream about doing great things. Engineers do them.", author: "James A. Michener" },
    { subject: "Genetics", quote: "Genetics is the study of the blueprint of life.", author: "Walter Gilbert" },
    { subject: "Geography", quote: "Geography is the study of Earth as the home of humanity.", author: "Yi-Fu Tuan" },
    { subject: "Graphic Design", quote: "Design is the silent ambassador of your brand.", author: "Paul Rand" },
    { subject: "History", quote: "History is a relentless master. It has no present, only the past rushing into the future.", author: "John F. Kennedy" },
    { subject: "Human Resource Management", quote: "Ask not what your country can do for you—ask what you can do for your country.", author: "John F. Kennedy" },
    { subject: "Illustration", quote: "Creativity takes courage.", author: "Henri Matisse" },
    { subject: "Interactive Digital Media & Game Development", quote: "Games are a series of interesting decisions.", author: "Sid Meier" },
    { subject: "Interior Design", quote: "The essence of interior design will always be about people and how they live.", author: "Albert Hadley" },
    { subject: "International Relations", quote: "Law is the essential foundation of stability and order in international relations.", author: "J. William Fulbright" },
    { subject: "Journalism", quote: "Journalism allows its readers to witness history.", author: "John Hersey" },
    { subject: "Law", quote: "Injustice anywhere is a threat to justice everywhere.", author: "Martin Luther King Jr." },
    { subject: "Linguistics", quote: "Language shapes the way we think, and determines what we can think about.", author: "Benjamin Lee Whorf" },
    { subject: "Literature", quote: "Literature is the one place where people can speak their original human mind.", author: "Allen Ginsberg" },
    { subject: "Management", quote: "Management is doing things right; leadership is doing the right things.", author: "Peter Drucker" },
    { subject: "Marine Biology", quote: "The natural world is the greatest source of excitement, visual beauty, and intellectual interest.", author: "Sir David Attenborough" },
    { subject: "Marketing", quote: "Good marketing makes the company look smart. Great marketing makes the customer feel smart.", author: "Joe Chernov" },
    { subject: "Mathematics", quote: "Mathematics is the most beautiful and most powerful creation of the human spirit.", author: "Stefan Banach" },
    { subject: "Mechanical Engineering", quote: "Engineering or technology is all about using the power of science to make life better for people.", author: "Neil Armstrong" },
    { subject: "Media Studies", quote: "The medium is the message.", author: "Marshall McLuhan" },
    { subject: "Medicine", quote: "The art of medicine consists of amusing the patient while nature cures the disease.", author: "Voltaire" },
    { subject: "Microbiology", quote: "Biology is the most powerful technology ever created.", author: "Arvind Gupta" },
    { subject: "Music", quote: "Music gives a soul to the universe, wings to the mind, flight to the imagination.", author: "Plato" },
    { subject: "Nursing", quote: "Save one life, you’re a hero. Save 100 lives, you’re a nurse.", author: "Florence Nightingale" },
    { subject: "Pharmacy", quote: "Wherever the art of medicine is loved, there is also a love of humanity.", author: "Hippocrates" },
    { subject: "Philosophy", quote: "The unexamined life is not worth living.", author: "Socrates" },
    { subject: "Physics", quote: "If I have seen further it is by standing on the shoulders of Giants.", author: "Isaac Newton" },
    { subject: "Physiotherapy", quote: "The body achieves what the mind believes.", author: "Muhammad Ali" },
    { subject: "Political Science", quote: "Politics is the art of the possible.", author: "Otto von Bismarck" },
    { subject: "Product & Industrial Design", quote: "Design is the art of turning constraints into opportunities.", author: "Dieter Rams" },
    { subject: "Psychology", quote: "The privilege of a lifetime is to become who you truly are.", author: "Joseph Campbell" },
    { subject: "Sociology", quote: "Sociology examines the structures that shape our lives and societies.", author: "Max Weber" },
    { subject: "Sports Science", quote: "Strength does not come from physical capacity. It comes from an indomitable will.", author: "Mahatma Gandhi" },
    { subject: "Statistics", quote: "Statistical thinking will one day be as necessary for efficient citizenship as the ability to read and write.", author: "H.G. Wells" },
{ 
    subject: "Theology", 
    quote: "The fear of the Lord is the beginning of wisdom, and the knowledge of the Holy One is understanding.", 
    author: "Proverbs 9:10" 
},
    { subject: "Veterinary Medicine", quote: "It is our responsibility to create a planet that provides a home for all life on Earth.", author: "Sir David Attenborough" },
    { subject: "Zoology", quote: "The natural world is the greatest source of excitement, visual beauty, and intellectual interest.", author: "Sir David Attenborough" }
];

   // Motivational Quotes Component - Now displays in random order
   const MotivationalQuotes = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [shuffledQuotes, setShuffledQuotes] = useState([]);

    // Shuffle quotes once on mount (Fisher-Yates shuffle)
    useEffect(() => {
      const shuffleArray = (array) => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
      };

      setShuffledQuotes(shuffleArray(motivationalQuotes));
    }, []);

    useEffect(() => {
      if (shuffledQuotes.length === 0) return;

      const interval = setInterval(() => {
        setIsVisible(false);   // Start fade out

        setTimeout(() => {
          setCurrentIndex((prev) => (prev + 1) % shuffledQuotes.length);
          setIsVisible(true);   // Start fade in
        }, 600);
      }, 5000); // Change quote every 5 seconds

      return () => clearInterval(interval);
    }, [shuffledQuotes]);

    if (shuffledQuotes.length === 0) return null;

    const quote = shuffledQuotes[currentIndex];

    return (
      <div 
        className="aspire-quote"
        style={{ opacity: isVisible ? 1 : 0 }}
      >
        “{quote.quote}”
        {quote.author && (
          <span className="aspire-quote-author"> — {quote.author}</span>
        )}
        <div className="aspire-quote-subject">{quote.subject}</div>
      </div>
    );
  };
  
export default function AuthForm({ type, onLogin, initialInviteSlug }) {
  const navigate = useNavigate();
  const location = useLocation(); 

  // Read invite params from URL (for invited parents/mentors)
  const urlParams = new URLSearchParams(location.search);
  const inviteToken = urlParams.get('inviteToken');
  const prefilledEmail = urlParams.get('email');
  const inviteType = urlParams.get('type'); // 'parent' or 'mentor'

  const isInviteRegistration = !!inviteToken && !!prefilledEmail && !!inviteType;
 
  const [inviteSlug, setInviteSlug] = useState(initialInviteSlug || "");
  const [email, setEmail] = useState(prefilledEmail || "");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const [password, setPassword] = useState("");
  const [age, setAge] = useState("");
  const [debouncedAge, setDebouncedAge] = useState("");
  const [error, setError] = useState(null);
  const [username, setUsername] = useState(""); // New state for username
  const [debouncedUsername, setDebouncedUsername] = useState(""); // New state for debounced username
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginButtonDisabled, setLoginButtonDisabled] = useState(false);
  const [forgotButtonDisabled, setForgotButtonDisabled] = useState(false);
  const [resendButtonDisabled, setResendButtonDisabled] = useState(false);
  const [loginButtonText, setLoginButtonText] = useState(type === "login" ? "Log In" : "Register");
  const [forgotButtonText, setForgotButtonText] = useState("Send Reset Link");
  const [resendButtonText, setResendButtonText] = useState("Resend Verification");
  const [warning, setWarning] = useState("");
  const [success, setSuccess] = useState("");   // ← ADD THIS LINE


  // Rate limiting refs
  const loginState = useRef({ attempts: 0, lastAttempt: 0, dailyResends: 0, lastResetDate: "" });
  const forgotState = useRef({ attempts: 0, lastAttempt: 0 });
  const resendState = useRef({ attempts: 0, lastAttempt: 0 });
  const registerState = useRef({ attempts: 0, lastAttempt: 0, lastResetDate: "" }); // Added for registration attempts
  const whitelistState = useRef({ lastAttempt: 0 });

  // Constants for limits
  const MAX_LOGIN_ATTEMPTS = 5;
  const MAX_FORGOT_REQUESTS = 3;
  const MAX_RESEND_ATTEMPTS = 3;
  const MAX_DAILY_RESENDS = 10;
  const MAX_REGISTER_ATTEMPTS = 5; // Added for registration attempts
  const LOGIN_COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes
  const FORGOT_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour
  const RESEND_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour
  const REGISTER_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
  const BASE_LOGIN_DELAY_MS = 2000; // 2 second base delay
  const BASE_FORGOT_DELAY_MS = 10000; // 10 second base delay
  const BASE_RESEND_DELAY_MS = 10000; // 10 second base delay
  const BASE_REGISTER_DELAY_MS = 2000; // 2 second base delay for registration

  // Local storage keys
  const [ipAddress, setIpAddress] = useState("unknown");
  const REGISTER_STORAGE_KEY = `auth_limits_ip_${ipAddress}`; // IP-based for registration
  const USER_STORAGE_KEY = `auth_limits_user_${debouncedEmail || "unknown"}`; // Email-based for user-specific actions

    // New state for navbar and video modal
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showWhitelistModal, setShowWhitelistModal] = useState(false);
  const WHITELIST_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour
  const WHITELIST_STORAGE_KEY = `auth_limits_whitelist_${ipAddress}`;
  const [whitelistButtonDisabled, setWhitelistButtonDisabled] = useState(false);
  const [whitelistButtonText, setWhitelistButtonText] = useState("Send Whitelist Request");
  const [whitelistMessage, setWhitelistMessage] = useState("");
const [showCareerAssessmentModal, setShowCareerAssessmentModal] = useState(false);
const [answers, setAnswers] = useState({});
const [result, setResult] = useState(null);
const [isSubmitted, setIsSubmitted] = useState(false);
const [currentQuestion, setCurrentQuestion] = useState(1);
const [showCelebration, setShowCelebration] = useState(false);
const quickAdvanceTimer = useRef(null); // guards against double-advancing on a fast double-click

    // You can add more YouTube video IDs here
  const youtubeVideos = [
    {
    id: "wMU0G6nMBWE",
    title: "Raising AI-Era Leaders",
    timestamp: 1   
  },
  {
    id: "UBlI29HfHxI",
    title: "Academic Enrichment & Career Planning",
    timestamp: 1   
  },
  {
    id: "mHnUFCiT6CE",
    title: "ASPIREdeck Consultations",
    timestamp: 1  
  },
  {
    id: "hgpT3bDZeuY",
    title: "Parent/Mentor Portal",
    timestamp: 1  
  } 
];

   // Toggle mobile menu
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

 // NEW: Open modal and reset previous answers
const openCareerAssessment = () => {
  if (quickAdvanceTimer.current) { clearTimeout(quickAdvanceTimer.current); quickAdvanceTimer.current = null; }
  setAnswers({});
  setResult(null);
  setIsSubmitted(false);
  setCurrentQuestion(1);
  setShowCareerAssessmentModal(true);
  if (isMenuOpen) closeMenu();
};


// Helper function to return all 10 questions - tailored for high school & university students
const getQuestion = (id) => {
  const questions = {
    1: {
      text: "Your perfect weekend starts with…",
      options: [
        { letter: "A", text: "Going on an adventure — hiking, road trip, or trying a new sport" },
        { letter: "B", text: "Hanging out with friends, hosting a get-together, or planning group activities" },
        { letter: "C", text: "Diving into a good book, watching educational videos, or working on a personal project" },
        { letter: "D", text: "Organising my room, planning my week, or helping family/friends with tasks" }
      ]
    },
    2: {
      text: "In a school or university group project, you naturally become the…",
      options: [
        { letter: "A", text: "Idea generator who suggests bold, creative approaches" },
        { letter: "B", text: "Motivator who keeps the team excited and communicates well" },
        { letter: "C", text: "Researcher who digs deep and finds the best information" },
        { letter: "D", text: "Organiser who makes sure everyone stays on track and deadlines are met" }
      ]
    },
    3: {
      text: "When learning something new for class or university, you prefer…",
      options: [
        { letter: "A", text: "Jumping in and trying things hands-on (trial and error)" },
        { letter: "B", text: "Discussing it with friends or explaining it to others" },
        { letter: "C", text: "Reading detailed notes, watching tutorials, or researching thoroughly" },
        { letter: "D", text: "Following clear step-by-step instructions or structured guides" }
      ]
    },
    4: {
      text: "Your friends or classmates would most likely describe you as…",
      options: [
        { letter: "A", text: "Energetic, adventurous, and always up for trying new things" },
        { letter: "B", text: "Friendly, outgoing, and great at bringing people together" },
        { letter: "C", text: "Thoughtful, knowledgeable, and a deep thinker" },
        { letter: "D", text: "Reliable, organised, and someone who gets things done" }
      ]
    },
    5: {
      text: "What drains you the most during school or university work?",
      options: [
        { letter: "A", text: "Long lectures or sitting still with no practical activity" },
        { letter: "B", text: "Working completely alone for long periods without interaction" },
        { letter: "C", text: "Vague instructions or group drama/politics" },
        { letter: "D", text: "Constant last-minute changes and disorganisation" }
      ]
    },
    6: {
      text: "You feel most proud when you…",
      options: [
        { letter: "A", text: "Overcome a difficult challenge, like mastering a tough subject or skill" },
        { letter: "B", text: "Help a friend or classmate understand something or succeed" },
        { letter: "C", text: "Solve a complex problem or figure out something others struggled with" },
        { letter: "D", text: "Create order from chaos — like organising notes or planning a big assignment" }
      ]
    },
    7: {
      text: "Your ideal study or workspace looks like…",
      options: [
        { letter: "A", text: "Somewhere active — outdoors, a lively café, or a makerspace" },
        { letter: "B", text: "A bright, collaborative space where I can talk with others" },
        { letter: "C", text: "A quiet corner with good lighting, books, and minimal distractions" },
        { letter: "D", text: "A clean, well-organised desk with everything neatly arranged" }
      ]
    },
    8: {
      text: "When facing a difficult assignment or problem, you first…",
      options: [
        { letter: "A", text: "Start experimenting and figure it out as you go" },
        { letter: "B", text: "Talk it through with friends or classmates for different ideas" },
        { letter: "C", text: "Research and analyse all the information available" },
        { letter: "D", text: "Make a detailed plan or checklist before starting" }
      ]
    },
    9: {
      text: "You get most excited about future careers or university subjects involving…",
      options: [
        { letter: "A", text: "Hands-on work, innovation, travel, or building/creating things" },
        { letter: "B", text: "Working with people, teaching, creativity, or helping others" },
        { letter: "C", text: "Research, technology, science, or deep intellectual exploration" },
        { letter: "D", text: "Structure, leadership, systems, or making processes more efficient" }
      ]
    },
    10: {
      text: "If money and time weren’t issues, you’d spend your days…",
      options: [
        { letter: "A", text: "Traveling, trying new experiences, or working on exciting projects" },
        { letter: "B", text: "Building communities, mentoring others, or doing creative group activities" },
        { letter: "C", text: "Learning everything possible about topics that fascinate you" },
        { letter: "D", text: "Designing better systems, organising events, or running smooth operations" }
      ]
    }
  };

  return questions[id] || questions[1];
};

// Calculate result from answers - with full mixed result support
const calculateResult = (userAnswers) => {
  const scores = { A: 0, B: 0, C: 0, D: 0 };
  
  // Count scores
  Object.values(userAnswers).forEach(answer => {
    if (scores[answer] !== undefined) {
      scores[answer]++;
    }
  });

  // Find primary and secondary
  const sortedScores = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const primaryLetter = sortedScores[0][0];
  const secondaryLetter = sortedScores[1][0];

  const typeMap = {
    A: "Explorer / Builder",
    B: "Connector / Inspirer",
    C: "Thinker / Investigator",
    D: "Organiser / Guardian"
  };

  let primaryType = typeMap[primaryLetter];
  let secondaryType = typeMap[secondaryLetter];
  let description = "";
  let careerTypes = "";

  // Handle mixed results with better descriptions
  const topTwo = primaryLetter + secondaryLetter;

  if (scores[primaryLetter] === scores[secondaryLetter]) {
    // Tie between top two
    if (topTwo === "AC" || topTwo === "CA") {
      primaryType = "Innovator / Tech Creator";
      description = "You combine curiosity with action. You love turning ideas into real solutions.";
      careerTypes = "Product Developer, Startup Founder, App Developer, Design Engineer";
    } 
    else if (topTwo === "BD" || topTwo === "DB") {
      primaryType = "People Leader";
      description = "You excel at both connecting with people and creating structure.";
      careerTypes = "Team Manager, HR Consultant, Project Coordinator, Teacher";
    } 
    else if (topTwo === "AB" || topTwo === "BA") {
      primaryType = "Dynamic Influencer";
      description = "You bring energy and connection together. People are naturally drawn to your enthusiasm.";
      careerTypes = "Content Creator, Trainer, Community Builder, Marketing Specialist";
    } 
    else if (topTwo === "CD" || topTwo === "DC") {
      primaryType = "Analyst / Specialist";
      description = "You combine deep thinking with strong organisational skills.";
      careerTypes = "Data Analyst, Researcher, Quality Control, Systems Analyst";
    }
    else if (topTwo === "BC" || topTwo === "CB") {
      primaryType = "Connector / Thinker";
      description = "You combine people skills with deep thinking and analysis. You excel at explaining complex ideas and building meaningful connections.";
      careerTypes = "Teacher, Consultant, Researcher, Counselor, Science Communicator, Community Educator";
    } 
    else if (topTwo === "AD" || topTwo === "DA") {
      primaryType = "Creative Organiser";
      description = "You blend hands-on creativity with strong structure and execution.";
      careerTypes = "Project Manager, Product Designer, Event Producer, Operations Lead, Architect";
    }
  } else {
    // Standard single dominant type
    switch (primaryLetter) {
      case "A":
        description = "You thrive on challenge, movement, and turning ideas into reality.";
        careerTypes = "Entrepreneur, Engineer, Architect, Pilot, Adventure Guide, Inventor";
        break;
      case "B":
        description = "You light up rooms and help people connect and grow.";
        careerTypes = "Teacher, Coach, Counselor, Marketing, Event Planner, HR";
        break;
      case "C":
        description = "You love solving puzzles and understanding how things work.";
        careerTypes = "Scientist, Researcher, Programmer, Writer, Analyst, Strategist";
        break;
      case "D":
        description = "You create order and make sure everything actually gets done.";
        careerTypes = "Project Manager, Administrator, Operations, Logistics, Accountant";
        break;
      default:
        description = "You have a unique mix of strengths.";
    }
  }

  return {
    primary_type: primaryType,
    secondary_type: secondaryType,
    scores: scores,
    description: description,
    careerTypes: careerTypes,
    dominant: primaryLetter
  };
};

// Handle answer selection — now auto-advances to the next question (or auto-submits on
// the last one) a beat after the pick, so clicking the right arrow is no longer required.
// Uses the functional setAnswers updater inside the timeout (not the outer `answers`/
// `submitAssessment` closure) so the just-picked answer is guaranteed to be included —
// calling the outer submitAssessment here would read a stale pre-click `answers` value.
const handleAnswer = (questionId, letter) => {
  if (quickAdvanceTimer.current) return; // ignore rapid double-clicks mid-transition
  setAnswers(prev => ({
    ...prev,
    [questionId]: letter
  }));
  quickAdvanceTimer.current = setTimeout(() => {
    quickAdvanceTimer.current = null;
    if (questionId < 10) {
      setCurrentQuestion(questionId + 1);
    } else {
      setAnswers(prev => {
        const calculatedResult = calculateResult(prev);
        setResult(calculatedResult);
        setIsSubmitted(true);
        localStorage.setItem('quickCareerAssessment', JSON.stringify({
          answers: prev,
          result: calculatedResult,
          timestamp: new Date().toISOString()
        }));
        triggerConfettiCelebration();
        return prev; // no change to answers itself — just used to read the latest value
      });
    }
  }, 300);
};

// Check if current question is answered
const isCurrentQuestionAnswered = () => {
  return !!answers[currentQuestion];
};

// Go to specific question
const goToQuestion = (q) => {
  if (q >= 1 && q <= 10) {
    setCurrentQuestion(q);
  }
};

// Submit assessment
const submitAssessment = () => {
  if (Object.keys(answers).length < 10) {
    alert("Please answer all 10 questions before submitting!");
    return;
  }

  const calculatedResult = calculateResult(answers);
  setResult(calculatedResult);
  setIsSubmitted(true);

  localStorage.setItem('quickCareerAssessment', JSON.stringify({
    answers,
    result: calculatedResult,
    timestamp: new Date().toISOString()
  }));

  // Trigger modern confetti celebration
  triggerConfettiCelebration();
};

// New function: Modern confetti celebration
const triggerConfettiCelebration = () => {
  setShowCelebration(true);

  // Realistic burst of confetti
  const count = 200;
  const defaults = {
    origin: { y: 0.7 }
  };

  function fire(particleRatio, opts) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio)
    });
  }

  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });

  // Optional second wave after a short delay for more impact
  setTimeout(() => {
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 }
    });
  }, 400);

  // Auto-hide the overlay after animation finishes
  setTimeout(() => {
    setShowCelebration(false);
  }, 4500);
};


  // Fetch IP address on component mount
useEffect(() => {
  const fetchIp = async () => {
    try {
      const res = await axios.get("https://api.ipify.org?format=json");
      setIpAddress(res.data.ip);
    } catch (err) {
      console.error("Failed to fetch IP address:", err);
    }
  };
  fetchIp();
}, []);

  // Save state to local storage
 const saveState = useCallback(() => {
  // Save registration state (IP-based)
  localStorage.setItem(
    REGISTER_STORAGE_KEY,
    JSON.stringify({
      register: registerState.current,
    })
  );

  // Save user-specific state (email-based)
  localStorage.setItem(
    USER_STORAGE_KEY,
    JSON.stringify({
      login: loginState.current,
      forgot: forgotState.current,
      resend: resendState.current,
    })
  );
 localStorage.setItem(
    WHITELIST_STORAGE_KEY,
    JSON.stringify({ whitelist: whitelistState.current })
  );
}, [REGISTER_STORAGE_KEY, USER_STORAGE_KEY, WHITELIST_STORAGE_KEY]);

// Add this function
const updateWhitelistButtonState = useCallback(() => {
  const now = Date.now();

  if (whitelistState.current.lastAttempt) {
    const timeSinceLastAttempt = now - whitelistState.current.lastAttempt;

    if (timeSinceLastAttempt < WHITELIST_COOLDOWN_MS) {
      const remaining = Math.ceil((WHITELIST_COOLDOWN_MS - timeSinceLastAttempt) / 1000);
      setWhitelistButtonDisabled(true);
      setWhitelistButtonText(`Wait ${remaining}s`);
      return;
    }
  }

  // Cooldown expired or no previous attempt
  setWhitelistButtonDisabled(false);
  setWhitelistButtonText("Send Whitelist Request");
}, []);

  // Calculate progressive delay
  const getProgressiveDelay = (attempts, baseDelay) => {
    return attempts >= 1 ? baseDelay * Math.pow(2, Math.min(attempts - 1, 4)) : 0;
  };

  
  // Reset daily resend count if new day
const checkDailyReset = useCallback(() => {
  const today = new Date().toISOString().split("T")[0];
  if (loginState.current.lastResetDate !== today) {
    loginState.current.dailyResends = 0;
    loginState.current.lastResetDate = today;
    saveState();
  }
}, [saveState]);

  // Reset daily resend count if new day
  const checkDailyRegisterReset = useCallback(() => {
  const today = new Date().toISOString().split("T")[0];
  if (registerState.current.lastResetDate !== today) {
    registerState.current.attempts = 0;
    registerState.current.lastResetDate = today;
    saveState();
  }
}, [saveState]);

  // Update button states and delays
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateButtonStates = useCallback(() => {
  const now = Date.now();
  checkDailyReset();
  checkDailyRegisterReset();

  // Login button (user-specific)
  if (type === "login" && loginState.current.attempts >= MAX_LOGIN_ATTEMPTS) {
    if (now - loginState.current.lastAttempt < LOGIN_COOLDOWN_MS) {
      setLoginButtonDisabled(true);
      const remaining = Math.ceil((LOGIN_COOLDOWN_MS - (now - loginState.current.lastAttempt)) / 1000);
      setLoginButtonText(`Wait ${remaining}s`);
      setWarning("Too many login attempts. Please wait.");
      return;
    } else {
      loginState.current.attempts = 0;
      saveState();
    }
  } else if (type === "login" && loginState.current.attempts > 0) {
    const delay = getProgressiveDelay(loginState.current.attempts, BASE_LOGIN_DELAY_MS);
    if (delay > 0 && now - loginState.current.lastAttempt < delay) {
      setLoginButtonDisabled(true);
      const remaining = Math.ceil((delay - (now - loginState.current.lastAttempt)) / 1000);
      setLoginButtonText(`Wait ${remaining}s`);
      if (loginState.current.attempts === MAX_LOGIN_ATTEMPTS - 1) {
        setWarning("One more failed login attempt will lock you out for 15 minutes.");
      }
    } else {
      setLoginButtonDisabled(false);
      setLoginButtonText("Log In");
    }
  } else {
    setLoginButtonDisabled(false);
    setLoginButtonText(type === "login" ? "Log In" : "Register");
    setWarning("");
  }

  // Register button (IP-based)
  if (type === "register" && registerState.current.attempts >= MAX_REGISTER_ATTEMPTS) {
    if (now - registerState.current.lastAttempt < REGISTER_COOLDOWN_MS) {
      setLoginButtonDisabled(true);
      const remaining = Math.ceil((REGISTER_COOLDOWN_MS - (now - registerState.current.lastAttempt)) / 1000);
      setLoginButtonText(`Wait ${remaining}s`);
      setWarning("Maximum registration attempts reached. Try again tomorrow.");
      return;
    } else {
      registerState.current.attempts = 0;
      saveState();
    }
  } else if (type === "register" && registerState.current.attempts > 0) {
    const delay = getProgressiveDelay(registerState.current.attempts, BASE_REGISTER_DELAY_MS);
    if (delay > 0 && now - registerState.current.lastAttempt < delay) {
      setLoginButtonDisabled(true);
      const remaining = Math.ceil((delay - (now - registerState.current.lastAttempt)) / 1000);
      setLoginButtonText(`Wait ${remaining}s`);
      if (registerState.current.attempts === MAX_REGISTER_ATTEMPTS - 1) {
        setWarning("One more failed registration attempt will lock you out for 24 hours.");
      }
    } else {
      setLoginButtonDisabled(false);
      setLoginButtonText("Register");
    }
  }

    // Forgot password button
    if (forgotState.current.attempts >= MAX_FORGOT_REQUESTS) {
    if (now - forgotState.current.lastAttempt < FORGOT_COOLDOWN_MS) {
      setForgotButtonDisabled(true);
      const remaining = Math.ceil((FORGOT_COOLDOWN_MS - (now - forgotState.current.lastAttempt)) / 1000);
      setForgotButtonText(`Wait ${remaining}s`);
      return;
    } else {
      forgotState.current.attempts = 0;
      saveState();
    }
  } else if (forgotState.current.attempts > 0) {
    const delay = getProgressiveDelay(forgotState.current.attempts, BASE_FORGOT_DELAY_MS);
    if (delay > 0 && now - forgotState.current.lastAttempt < delay) {
      setForgotButtonDisabled(true);
      const remaining = Math.ceil((delay - (now - forgotState.current.lastAttempt)) / 1000);
      setForgotButtonText(`Wait ${remaining}s`);
      if (forgotState.current.attempts === MAX_FORGOT_REQUESTS - 1) {
        setWarning("One more forgot password request will lock you out for 1 hour.");
      }
    } else {
      setForgotButtonDisabled(false);
      setForgotButtonText("Send Reset Link");
    }
  } else {
    setForgotButtonDisabled(false);
    setForgotButtonText("Send Reset Link");
  }

    // Resend verification button
  if (resendState.current.attempts >= MAX_RESEND_ATTEMPTS || loginState.current.dailyResends >= MAX_DAILY_RESENDS) {
    if (now - resendState.current.lastAttempt < RESEND_COOLDOWN_MS) {
      setResendButtonDisabled(true);
      const remaining = Math.ceil((RESEND_COOLDOWN_MS - (now - resendState.current.lastAttempt)) / 1000);
      setResendButtonText(`Wait ${remaining}s`);
      return;
    } else {
      resendState.current.attempts = 0;
      saveState();
    }
  } else if (resendState.current.attempts > 0) {
    const delay = getProgressiveDelay(resendState.current.attempts, BASE_RESEND_DELAY_MS);
    if (delay > 0 && now - resendState.current.lastAttempt < delay) {
      setResendButtonDisabled(true);
      const remaining = Math.ceil((delay - (now - resendState.current.lastAttempt)) / 1000);
      setResendButtonText(`Wait ${remaining}s`);
      if (resendState.current.attempts === MAX_RESEND_ATTEMPTS - 1) {
        setWarning("One more resend attempt will lock you out for 1 hour.");
      }
    } else {
      setResendButtonDisabled(false);
      setResendButtonText("Resend Verification");
      setWarning(
        resendState.current.attempts >= MAX_RESEND_ATTEMPTS - 2 || loginState.current.dailyResends >= MAX_DAILY_RESENDS - 2
          ? "Approaching resend attempt limit."
          : ""
      );
    }
  } else {
    setResendButtonDisabled(false);
    setResendButtonText("Resend Verification");
  }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [checkDailyReset, checkDailyRegisterReset, saveState, type]);

useEffect(() => {
  const interval = setInterval(updateWhitelistButtonState, 1000);
  return () => clearInterval(interval);
}, [updateWhitelistButtonState]);

  // Update button states periodically
  useEffect(() => {
    const interval = setInterval(updateButtonStates, 1000);
    return () => clearInterval(interval);
  }, [updateButtonStates]);

  // ── BUG FIX ──────────────────────────────────────────────────────────────
  // Switching between "Log In" and "Register" via the in-page links calls
  // navigate(..., { replace: true }) on the SAME mounted <AuthForm/> instance
  // (the route just swaps the `type` prop) — React does not unmount/remount
  // the component, so every piece of local state used to carry straight over:
  // a stale error/success banner, a typed password, a checked "accept terms"
  // box, and a rate-limit button label/disabled state computed for the OTHER
  // mode would all still be showing after the switch. This effect clears that
  // transient state the moment `type` actually changes, and immediately
  // recomputes the submit button's label instead of waiting up to 1s for the
  // polling interval in updateButtonStates() to catch up.
  const previousTypeRef = useRef(type);
  useEffect(() => {
    if (previousTypeRef.current === type) return;
    previousTypeRef.current = type;

    setError(null);
    setSuccess("");
    setWarning("");
    setPassword("");
    setUsername("");
    setAge("");
    setAcceptedTerms(false);
    setShowPassword(false);
    setShowForgotPassword(false);
    setShowTermsModal(false);

    setLoginButtonDisabled(false);
    setLoginButtonText(type === "login" ? "Log In" : "Register");
  }, [type]);
  // ─────────────────────────────────────────────────────────────────────────

  // Sanitize input
  const sanitizeInput = (input) => {
    return sanitizeHtml(input, {
      allowedTags: [],
      allowedAttributes: {},
    }).trim();
  };

  // Validate password strength
  const validatePassword = (pwd) => {
    return passwordRegex.test(pwd);
  };

  // Debounce inputs
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmail(sanitizeInput(email));
    }, 300);
    return () => clearTimeout(handler);
  }, [email]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAge(sanitizeInput(age));
    }, 300);
    return () => clearTimeout(handler);
  }, [age]);

    useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedUsername(sanitizeInput(username));
    }, 300);
    return () => clearTimeout(handler);
  }, [username]);

    useEffect(() => {
    if (isInviteRegistration && email) {
      document.querySelector('input[type="password"]')?.focus();
    }
  }, [isInviteRegistration, email]);

  // Load state from local storage
useEffect(() => {
  // Load registration state (IP-based)
  const registerSaved = localStorage.getItem(REGISTER_STORAGE_KEY);
  if (registerSaved) {
    const parsed = JSON.parse(registerSaved);
    registerState.current = parsed.register || registerState.current;
  }

  // Load user-specific state (email-based)
  const userSaved = localStorage.getItem(USER_STORAGE_KEY);
  if (userSaved) {
    const parsed = JSON.parse(userSaved);
    loginState.current = parsed.login || loginState.current;
    forgotState.current = parsed.forgot || forgotState.current;
    resendState.current = parsed.resend || resendState.current;
  }
  updateButtonStates();

 const whitelistSaved = localStorage.getItem(WHITELIST_STORAGE_KEY);
  if (whitelistSaved) {
    const parsed = JSON.parse(whitelistSaved);
    whitelistState.current = parsed.whitelist || whitelistState.current;
  }

  updateWhitelistButtonState();
}, [ipAddress, debouncedEmail, checkDailyReset, REGISTER_STORAGE_KEY, USER_STORAGE_KEY, updateButtonStates]);

// eslint-disable-next-line react-hooks/exhaustive-deps
const handleSubmit = useCallback(
  async (e) => {
    e.preventDefault();
    const now = Date.now();

    // ── Rate limiting checks (unchanged) ──
    if (type === "register") {
      checkDailyRegisterReset();
      if (registerState.current.attempts >= MAX_REGISTER_ATTEMPTS) {
        if (now - registerState.current.lastAttempt < REGISTER_COOLDOWN_MS) {
          setError(
            `Too many registration attempts from this IP. Please wait ${Math.ceil(
              (REGISTER_COOLDOWN_MS - (now - registerState.current.lastAttempt)) / 1000
            )} seconds.`
          );
          return;
        }
        registerState.current.attempts = 0;
      }
    }

    if (type === "login" && loginState.current.attempts >= MAX_LOGIN_ATTEMPTS) {
      if (now - loginState.current.lastAttempt < LOGIN_COOLDOWN_MS) {
        setError(
          `Too many login attempts for this email. Please wait ${Math.ceil(
            (LOGIN_COOLDOWN_MS - (now - loginState.current.lastAttempt)) / 1000
          )} seconds.`
        );
        return;
      }
      loginState.current.attempts = 0;
    }

    // ── Input validation ──
    if (!emailRegex.test(debouncedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (type === "register") {
      // Validate the live value, not a debounced copy, so paste/autofill + instant submit works
      if (!validatePassword(password)) {
                setError("Password must be at least 8 characters and include uppercase, lowercase, number, and symbol.");
        return;
      }

      if (!usernameRegex.test(debouncedUsername)) {
        setError("Username must be 3–50 characters long and contain only letters, numbers, or underscores.");
        return;
      }

      if (!acceptedTerms) {
        setError("You must accept the terms and conditions.");
        return;
      }

      const ageNum = parseInt(debouncedAge);
      if (isNaN(ageNum) || ageNum < 13) {
        setError("You must be at least 13 years old to register.");
        return;
      }

    // Whitelist check — only for normal registration
    if (type === "register" && !isInviteRegistration && !inviteSlug) {
      try {
        const whitelistRes = await axios.post('/api/whitelist/check-email', {
          email: debouncedEmail,
        });
        if (!whitelistRes.data.isWhitelisted) {
          setError(
            "This app is currently available to selected school email addresses and by invitation only. Please contact support@oxbridgegateway.com if you would like your school to be added to the system."
          );
          registerState.current.attempts++;
          registerState.current.lastAttempt = now;
          saveState();
          updateButtonStates();
          return;
        }
      } catch (err) {
        console.error("Whitelist check failed:", err);
        setError("Failed to verify email whitelist status. Please try again.");
        registerState.current.attempts++;
        registerState.current.lastAttempt = now;
        saveState();
        updateButtonStates();
        return;
      }
    }
    }

    // ── Prepare payload ──
   const payload = type === "register"
      ? { 
          email: debouncedEmail, 
          password,
                    age: debouncedAge, 
          username: debouncedUsername,
          inviteSlug, // normal flow (if any)
          ...(isInviteRegistration && {
            inviteToken,
            inviteType,
          }),
        }
      : { email: debouncedEmail, password };

    // ── Choose correct endpoint ──
    let endpoint;
    if (type === "login") {
      endpoint = '/api/users/login';
    } else if (isInviteRegistration) {
      endpoint = '/api/users/register-via-invite'; // ← Use the dedicated invite endpoint
    } else {
      endpoint = '/api/users/register'; // normal registration
    }

    // ── Submit ──
    try {
      const res = await axios.post(endpoint, payload);

      if (type === "login") {
  const { token, user_id, role, usertype  } = res.data; // ← IMPORTANT: expect role from backend

  if (!token || !user_id) {
    throw new Error("Invalid login response.");
  }

  localStorage.setItem("token", token);
  localStorage.setItem("jwtToken", token);   // ← backup key for bookmark redirect

  localStorage.setItem("userId", user_id);
  
  // Save role to localStorage so Dashboard/MyChildren can read it instantly
  const userRole = role || "user"; // fallback just in case
  localStorage.setItem("role", userRole);
 localStorage.setItem("usertype", usertype || "standard");

  loginState.current.attempts = 0;
  saveState();

  if (onLogin) onLogin(user_id, token);

  // NEW: Check if we came from an invite acceptance redirect
  const inviteToken = location.state?.inviteToken;
  if (inviteToken) {
    console.log("Auto-accepting pending invite after login:", inviteToken);

    try {
      await axios.post(
        '/api/users/relationships/accept',
        { token: inviteToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Auto-accept successful!");
      // Optional: show success toast or message
    } catch (acceptErr) {
      console.error("Auto-accept failed after login:", acceptErr);
      // Don't block login — just log error
    }
  }


  // ── ROLE-BASED REDIRECT ──
  if (userRole === "parent" || userRole === "mentor") {
    navigate("/my-children", { replace: true });
  } else {
    navigate("/dashboard", { replace: true });
  }
   } else {
        // Registration success (both normal and invite)
        setSuccess(res.data.message);

        const redirectDelay = res.data.autoVerified
          ? 1500
          : (isInviteRegistration ? 5000 : 4000);

        setTimeout(() => {
          navigate("/login", { replace: true });
        }, redirectDelay);

        registerState.current.attempts = 0;
        saveState();
      }

      updateButtonStates();

    } catch (err) {
      console.error("Registration/login error:", {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });

      const errorMsg = err.response?.data?.error || "Something went wrong.";

      if (err.response?.status === 403 && errorMsg.includes("waiting list")) {
        setError(errorMsg);
      } else if (errorMsg.includes("already registered")) {
        setError("This email is already registered. Please log in instead.");
      } else if (errorMsg.includes("Invalid, expired, or already used invitation token")) {
        setError("This invitation link is invalid or has expired. Please ask the student to resend the invitation.");
      } else {
        setError(errorMsg);
      }

      if (type === "login") {
        loginState.current.attempts++;
        loginState.current.lastAttempt = now;
      } else {
        registerState.current.attempts++;
        registerState.current.lastAttempt = now;
      }
      saveState();
      updateButtonStates();
    }
  },
  [
    type, debouncedEmail, password, debouncedAge, debouncedUsername,
        acceptedTerms, inviteSlug, isInviteRegistration, inviteToken, inviteType,
    onLogin, navigate, checkDailyRegisterReset, saveState, updateButtonStates
  ]
);

// eslint-disable-next-line react-hooks/exhaustive-deps
const handleResendVerification = useCallback(
  async () => {
    const now = Date.now();
    checkDailyReset();

    if (loginState.current.dailyResends >= MAX_DAILY_RESENDS) {
      setError("Maximum daily resend limit reached for this email. Please try again tomorrow.");
      return;
    }

    if (resendState.current.attempts >= MAX_RESEND_ATTEMPTS) {
      if (now - resendState.current.lastAttempt < RESEND_COOLDOWN_MS) {
        setError(`Too many resend attempts for this email. Please wait ${Math.ceil((RESEND_COOLDOWN_MS - (now - resendState.current.lastAttempt)) / 1000)} seconds.`);
        return;
      }
      resendState.current.attempts = 0;
    }

    if (!emailRegex.test(debouncedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    resendState.current.attempts++;
    resendState.current.lastAttempt = now;
    loginState.current.dailyResends++;
    saveState();
    updateButtonStates();
    setError(null);

    try {
      await axios.post('/api/users/resend-verification', { email: debouncedEmail });
      setError("Verification email resent successfully. Please check your inbox.");
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to resend verification email.";
      setError(errorMsg);
    }
  },
  [debouncedEmail, checkDailyReset, saveState, updateButtonStates]
);

// eslint-disable-next-line react-hooks/exhaustive-deps
const handleForgotPassword = useCallback(
  async () => {
    const now = Date.now();

    if (forgotState.current.attempts >= MAX_FORGOT_REQUESTS) {
      if (now - forgotState.current.lastAttempt < FORGOT_COOLDOWN_MS) {
        setError(`Too many forgot password requests for this email. Please wait ${Math.ceil((FORGOT_COOLDOWN_MS - (now - forgotState.current.lastAttempt)) / 1000)} seconds.`);
        return;
      }
      forgotState.current.attempts = 0;
    }

    if (!emailRegex.test(debouncedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    forgotState.current.attempts++;
    forgotState.current.lastAttempt = now;
    saveState();
    updateButtonStates();
    setError(null);

    try {
      await axios.post('/api/users/forgot-password', { email: debouncedEmail });
      setError("Password reset email sent successfully. Please check your inbox.");
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to send password reset email.";
      setError(errorMsg);
    }
  },
  [debouncedEmail, saveState, updateButtonStates]
);

useEffect(() => {
  document.body.classList.add('specific-page-auth');
  return () => document.body.classList.remove('specific-page-auth');
}, []);
  


return (
  <>
   {/* ==================== SEO METADATA (NEW) ==================== */}
<Helmet>
  <title>ASPIREdeck | Free Career & University Guidance for Students</title>
  <meta
    name="description"
    content="Free psychometric assessments for students 13+. Discover your ideal university subjects and AI-era career path. Built by Oxford & Cambridge academics."
  />
  <meta
    name="keywords"
    content="free career assessment students, university subject guidance, psychometric test teenagers, AI career readiness, Oxbridge Gateway, UniReady, StudyStyle, FutureReady, AIReady, A-level subject choice, career quiz students, ASPIREdeck"
  />
  <link
    rel="canonical"
    href={`https://www.aspiredeck.com/${type === "login" ? "login" : "register"}`}
  />

  {/* Open Graph */}
  <meta property="og:title" content="ASPIREdeck – Free Career & University Guidance Platform" />
  <meta
    property="og:description"
    content="Take our Career QuickScan and explore evidence-based assessments by Oxbridge experts. Choose the right university subjects and thrive in the AI economy."
  />
  <meta property="og:type" content="website" />
  <meta
    property="og:url"
    content={`https://www.aspiredeck.com/${type === "login" ? "login" : "register"}`}
  />
  <meta property="og:image" content="https://www.aspiredeck.com/og-image.jpg" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  <meta property="og:site_name" content="ASPIREdeck by Oxbridge Gateway" />

  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="ASPIREdeck | Free Psychometric Career Guidance" />
  <meta
    name="twitter:description"
    content="Instant Career QuickScan + full AI-era assessments for students 13+. Built with Oxbridge expertise."
  />
  <meta name="twitter:image" content="https://www.aspiredeck.com/og-image.jpg" />

  <meta name="robots" content="index, follow" />
  <meta name="theme-color" content="#0b1220" />
</Helmet>


    <div className="aspire-page">
      <div className="aspire-bg-glow aspire-bg-glow--1" />
      <div className="aspire-bg-glow aspire-bg-glow--2" />
      <div className="aspire-ring" />

      {/* ==================== NAV ==================== */}
      <nav className="aspire-nav">
        <div className="aspire-nav-inner">
          <a href="https://oxbridgegateway.com/aspiredeck" className="aspire-logo">
            <div className="auth-dashboard-logo logo-adjustment-score-auth "></div>
            <div className="OG-logo-auth">
            <span className="aspire-logo-text">ASPIRE<em>deck</em></span>
          </div>
          </a>

          <div className="aspire-nav-links">
            <a
              href="https://www.oxbridgegateway.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Oxbridge Gateway
            </a>
            <button onClick={() => setShowVideoModal(true)}>
              <FaPlay /> Watch Videos
            </button>
            <button onClick={openCareerAssessment}>
              <FaBolt /> CareerPulse
            </button>
               <button onClick={() => navigate("/subject-scan")}>
              <FaBrain /> Subject Scan
            </button>
            <button className="aspire-nav-cta" onClick={() => setShowWhitelistModal(true)}>
              <FaConciergeBell /> Request Whitelist
            </button>
          </div>

          <button className="aspire-hamburger" onClick={toggleMenu} aria-label="Menu">
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="aspire-mobile-menu">
            <a
              href="https://www.oxbridgegateway.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
            >
              Oxbridge Gateway
            </a>
            <button onClick={() => { setShowVideoModal(true); closeMenu(); }}>
              <FaPlay /> Watch Videos
            </button>
            <button onClick={openCareerAssessment}>
              <FaBolt /> CareerPulse
            </button>
                    <button onClick={() => navigate("/subject-scan")}>
              <FaBrain /> Subject Scan
            </button>
            <button onClick={() => { setShowWhitelistModal(true); closeMenu(); }}>
              <FaConciergeBell /> Request Whitelist
            </button>
          </div>
        )}
      </nav>

      {/* ==================== HERO / SPLIT ==================== */}
      <main className="aspire-main">
        {/* ---------- LEFT: brand messaging ---------- */}
        <section className="aspire-hero-left">
          <div>
            <p className="aspire-eyebrow">AI-Era University & Future Readiness Platform</p>
            <h1 className="aspire-hero-title">
              <span className="line1">Discover who<br />you are.</span>
              <span className="line2">Build who<br />you can become.</span>
            </h1>
          </div>

          <div className="aspire-hero-bottom">
            <p>
              <span className="aspire-hero-bottom-strong">Free, personalised intelligence</span> to help you understand yourself, develop your strengths, explore learning and career possibilities, and build the human capabilities to thrive in an AI-shaped future.
                 </p>
               <p>
                <br/>
              <span className="aspire-hero-bottom-strong">Built by Oxbridge academics for students aged 13+</span>
            </p>
          </div>
        </section>

        {/* ---------- RIGHT: auth panel ---------- */}
        <section className="aspire-hero-right">
          <div className="aspire-auth-panel">
            {isInviteRegistration && (
              <div className="aspire-invite-banner">
                <strong>Invited {inviteType === 'mentor' ? 'Mentor' : 'Parent'}</strong><br />
                You're registering to connect with a student.
              </div>
            )}

            <div className="aspire-panel-anim" key={type}>
            <h2 className="aspire-panel-heading">
              {type === "login" ? "Welcome back" : "Create your account"}
            </h2>
            <p className="aspire-panel-sub">
              {type === "login"
                ? "Log in to continue your personal development and explore new empowerment opportunities."
                : "Start your free journey with ASPIREdeck's evidence-based assessments."}
            </p>

            {success && (
              <p className="aspire-success">
                <FaCheckCircle aria-hidden="true" />
                <span>{success}</span>
              </p>
            )}

            {error && (
              <p className={`aspire-error ${error.includes("waiting list") ? "waiting-list" : ""}`}>
                {error.includes("waiting list") ? (
                  <FaCheckCircle aria-hidden="true" />
                ) : (
                  <FaExclamationCircle aria-hidden="true" />
                )}
                <span>{error}</span>
                {error === "Please verify your email before logging in." && type === "login" && (
                  <button
                    className="aspire-terms-link"
                    onClick={handleResendVerification}
                    disabled={resendButtonDisabled}
                  >
                    {resendButtonText}
                  </button>
                )}
              </p>
            )}

            {warning && (
              <p className="aspire-warning">
                <FaExclamationTriangle aria-hidden="true" />
                <span>{warning}</span>
              </p>
            )}

            <form onSubmit={handleSubmit} className="aspire-form" noValidate>
              {type === "register" && (
                <>
                  <p className="aspire-note">Your email must be whitelisted to register</p>
                  <div className="aspire-input-group">
                    <FaUserCircle className="aspire-input-icon" aria-hidden="true" />
                    <input
                      id="aspire-username"
                      type="text"
                      placeholder="Username"
                      aria-label="Username"
                      value={username}
                      onChange={(e) => setUsername(sanitizeInput(e.target.value))}
                      autoComplete="username"
                      required
                    />
                  </div>
                  <div className="aspire-input-group">
                    <FaCalendarAlt className="aspire-input-icon" aria-hidden="true" />
                    <input
                      id="aspire-age"
                      type="number"
                      placeholder="Age"
                      aria-label="Age"
                      value={age}
                      onChange={(e) => setAge(sanitizeInput(e.target.value))}
                      min="13"
                      required
                    />
                  </div>
                </>
              )}

              <div className="aspire-input-group">
                <FaEnvelope className="aspire-input-icon" aria-hidden="true" />
                <input
                  id="aspire-email"
                  type="email"
                  placeholder="Email"
                  aria-label="Email"
                  value={email}
                  onChange={(e) => setEmail(sanitizeInput(e.target.value))}
                  readOnly={isInviteRegistration}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="aspire-input-group">
                <FaLock className="aspire-input-icon" aria-hidden="true" />
                <input
                  id="aspire-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  aria-label="Password"
                  value={password}
                  // Do NOT sanitize passwords: sanitize-html turns "&" into "&amp;" (and trims),
                  // which corrupts the value and makes it fail the regex. Passwords are only
                  // ever hashed server-side, never rendered as HTML, so there is no XSS risk.
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={type === "login" ? "current-password" : "new-password"}
                  required
                />
                <button
                  type="button"
                  className="aspire-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              {type === "register" && inviteSlug && (
                <div className="aspire-invite-note">
                  Registering with invitation: <strong>{inviteSlug}</strong>
                </div>
              )}

              {type === "register" && (
                <>
                  <p className="aspire-password-requirements">
                    Your password must be at least 8 characters long, with no spaces, including one uppercase letter, one lowercase letter, one number and one special character (for example ! @ # $ % & * - _).
                                      </p>
                  <div className="aspire-terms-row">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      required
                    />
                    <label style={{color: 'white'}} htmlFor="terms">I agree to the</label>
                    <button
                      type="button"
                      className="aspire-terms-link"
                      onClick={() => setShowTermsModal(true)}
                    >
                      Terms and Conditions
                    </button>
                  </div>
                </>
              )}

              {type === "login" && (
                <button
                  type="button"
                  className="aspire-forgot-btn"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot Password?
                </button>
              )}

              <button type="submit" className="aspire-submit-btn" disabled={loginButtonDisabled}>
                {loginButtonDisabled && <span className="aspire-btn-spinner" aria-hidden="true" />}
                {loginButtonText}
              </button>
            </form>
            </div>

            <p className="aspire-switch">
              {type === "login" ? (
                <a
                  href="/register"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/register", { replace: true });
                  }}
                >
                  Don't have an account? Register
                </a>
              ) : (
                <a
                  href="/login"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/login", { replace: true });
                  }}
                >
                  Already have an account? Log In
                </a>
              )}
            </p>
          </div>
        </section>
      </main>

      {/* ==================== QUOTE FOOTER ==================== */}
      <footer className="aspire-quote-footer">
        <MotivationalQuotes />
      </footer>

      {/* Terms and Conditions Modal */}
        {showTermsModal && (
          <div className="modal">
            <div className="modal-content-terms">
              <h2>Terms and Conditions</h2>
              <p>
                Welcome to our psychometric assessment platform designed to help students aged 13 and over explore their higher education subject preferences, potential career paths, and study styles.
              </p>
              <p>
                By using this service, you agree to the following terms:
              </p>
              <ul>
                <li>
                  <strong>Purpose:</strong> The assessments provided are for informational and educational guidance only. They do not guarantee admission to any institution or success in any career and should not be considered legally binding advice.
                </li>
                <br/>
                <li>
                  <strong>Eligibility:</strong> The platform is intended for users aged 13 and over. If you are under 18, you must have permission from a parent or legal guardian to use this service.
                </li>
                <br/>
                <li>
                  <strong>Data Protection:</strong> We collect and process personal data in accordance with the General Data Protection Regulation (GDPR). All data collected is used solely for the purpose of generating assessment results and improving our service.
                </li>
                <br/>
                <li>
                  <strong>User Responsibility:</strong> You are responsible for the accuracy of the information you provide. Misleading or false input may affect the usefulness of the results.
                </li>
                <br/>
                <li>
                  <strong>No Legal Liability:</strong> The results and suggestions provided by the assessment are not definitive, and we accept no legal liability for any decisions or outcomes based on these results.
                </li>
                <br/>
                <li>
                  <strong>Changes to Terms:</strong> We reserve the right to update these terms at any time. Continued use of the service constitutes your acceptance of any changes.
                </li>
              </ul>
               <p>
            For more details, please read the{' '}
            <a 
              href="https://www.oxbridgegateway.com/termsandconditions" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#36a7f8', textDecoration: 'underline' }}
            >
              Terms and Conditions &amp; Disclaimer
            </a>,{' '}
            <a 
              href="https://www.oxbridgegateway.com/privacypolicy" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#36a7f8', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>, and{' '}
            <a 
              href="https://www.oxbridgegateway.com/cookiepolicy" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#36a7f8', textDecoration: 'underline' }}
            >
              Cookie Policy
            </a> 
            {' '}of Oxbridge Gateway Ltd.
          </p>
              <button
                className="modal-close"
                onClick={() => setShowTermsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="modal">
            <div className="modal-content">
              <h2>Reset Password</h2>
              <p>Enter your email to receive a password reset link.</p>
              <input
                type="email"
                placeholder="Email"
                className="auth-input-email"
                value={email}
                onChange={(e) => setEmail(sanitizeInput(e.target.value))}
                readOnly={isInviteRegistration}
                required
              />
              <button
                className="modal-button"
                onClick={handleForgotPassword}
                disabled={forgotButtonDisabled}
              >
                {forgotButtonText}
              </button>
              <button
                className="modal-close"
                onClick={() => setShowForgotPassword(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

              {/* ==================== VIDEO CAROUSEL MODAL (Full Page Overlay) ==================== */}
        {showVideoModal && (
          <div className="video-modal-overlay" onClick={() => setShowVideoModal(false)}>
            <div className="video-modal-content" onClick={e => e.stopPropagation()}>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowVideoModal(false)}
              >
                ✕
              </button>

              <div className="video-carousel-container">
                <button 
                  className="carousel-arrow carousel-arrow-left"
                  onClick={() => {
                    const carousel = document.querySelector('.video-carousel');
                    if (carousel) carousel.scrollBy({ left: -420, behavior: 'smooth' });
                  }}
                >
                  ←
                </button>

                <div className="video-carousel">
                  {youtubeVideos.map((video, index) => (
                    <div key={index} className="video-carousel-item">
                      <h3 className="video-title">{video.title}</h3>
                      <div className="youtube-video-wrapper">
                        <iframe
                        className="iframe"
                          width="100%"
                          height="500"
                          src={`https://www.youtube.com/embed/${video.id}?start=${video.timestamp}`}
                          title={video.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                  ))}
                </div>

                <button 
                  className="carousel-arrow carousel-arrow-right"
                  onClick={() => {
                    const carousel = document.querySelector('.video-carousel');
                    if (carousel) carousel.scrollBy({ left: 420, behavior: 'smooth' });
                  }}
                >
                  →
                </button>
              </div>
            </div>
          </div>
        )}

    {/* ==================== STRUCTURED DATA (JSON-LD) – NEW ==================== */}
    <script type="application/ld+json">
    {JSON.stringify([

      /* ── 1. WebApplication ── */
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "ASPIREdeck",
        "alternateName": ["ASPIRE deck", "ASPIREdeck.com"],
        "url": "https://www.aspiredeck.com",
        "applicationCategory": "EducationApplication",
        "applicationSubCategory": "Career Guidance, Psychometric Assessment, University Admissions",
        "operatingSystem": "All",
        "browserRequirements": "Requires JavaScript. Works in all modern browsers.",
        "inLanguage": "en",
        "isAccessibleForFree": true,
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock",
          "description": "Completely free for all users globally via email whitelist registration."
        },
        "description": "ASPIREdeck is a free web platform by Oxbridge Gateway empowering secondary and university students worldwide through four evidence-based psychometric assessments — UniReady™, StudyStyle™, FutureReady™ and AIReady™ — helping them discover ideal university subjects and build capabilities for the AI economy.",
        "screenshot": "https://www.aspiredeck.com/og-image.jpg",
        "featureList": [
          "UniReady™ — Academic interest profiling across 11 disciplinary clusters and 47 university subjects",
          "StudyStyle™ — Cognitive and learning preference profiling across 5 domains",
          "FutureReady™ — Career alignment assessment covering personality, emotional intelligence and work values",
          "AIReady™ — AI economy readiness assessment using the Oxbridge Gateway CREATIVE framework",
          "Enrich+™ — Academic enrichment hub with AI-personalised study strategies",
          "FutureLab™ — Career pathway module including Compass™, PortfolioX™ and InterviewPro™",
          "SparkHub™ — Human capability development module for AI-era leadership",
          "Parent and mentor portal — transparent progress visibility for families and educators",
          "School and institution dashboard — evidence-based guidance at scale"
        ],
        "audience": {
          "@type": "EducationalAudience",
          "educationalRole": "student",
          "suggestedMinAge": "13",
          "suggestedMaxAge": "25"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Oxbridge Gateway",
          "url": "https://www.oxbridgegateway.com/"
        }
      },

      /* ── 2. Organization ── */
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Oxbridge Gateway",
        "alternateName": "Oxbridge Gateway Limited",
        "legalName": "Oxbridge Gateway Limited",
        "url": "https://www.oxbridgegateway.com/",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.oxbridgegateway.com/logo.png",
          "width": 200,
          "height": 60
        },
        "image": "https://www.aspiredeck.com/og-image.jpg",
        "description": "Oxbridge Gateway is a world leader in higher education, university admissions and careers consultancy. Led by senior Oxbridge-credentialed academics, psychologists and education experts, it empowers the next generation through evidence-based guidance and ASPIREdeck.",
        "foundingLocation": {
          "@type": "Place",
          "name": "London, United Kingdom"
        },
        "identifier": {
          "@type": "PropertyValue",
          "name": "Companies House Number",
          "value": "12447163"
        },
        "address": [
          {
            "@type": "PostalAddress",
            "streetAddress": "Suite 4799, Unit 3A, 34-35 Hatton Garden",
            "addressLocality": "London",
            "postalCode": "EC1N 8DX",
            "addressCountry": "GB",
            "addressRegion": "England"
          },
          {
            "@type": "PostalAddress",
            "streetAddress": "Room 3208, 32nd Floor, Central Plaza, 18 Harbour Road",
            "addressLocality": "Wanchai",
            "addressCountry": "HK"
          }
        ],
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "email": "info@oxbridgegateway.com",
            "contactType": "General Enquiries"
          },
          {
            "@type": "ContactPoint",
            "email": "product@aspiredeck.com",
            "contactType": "User Registration and Product Support"
          }
        ],
        "telephone": "+447877722980",
        "sameAs": [
          "https://www.linkedin.com/company/oxbridge-gateway",
          "https://www.instagram.com/oxbridgegateway",
          "https://www.facebook.com/oxbridgegateway",
          "https://www.aspiredeck.com"
        ]
      },

      /* ── 3. EducationalOrganization ── */
      {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "name": "Oxbridge Gateway",
        "url": "https://www.oxbridgegateway.com/",
        "description": "Oxbridge Gateway provides world-class higher education and careers consultancy. Its ASPIREdeck platform delivers evidence-based psychometric assessments designed by Cambridge and Oxford academics to guide students aged 13+ towards their ideal university subjects and future careers.",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "ASPIREdeck Assessment Modules",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Course",
                "name": "UniReady™ — Academic Profiling Assessment",
                "description": "Three-stage psychometric assessment mapping students' academic interests across 11 disciplinary clusters and 47 university subjects.",
                "provider": { "@type": "Organization", "name": "Oxbridge Gateway" },
                "educationalLevel": "Secondary, Undergraduate",
                "audience": { "@type": "EducationalAudience", "educationalRole": "student" }
              },
              "price": "0",
              "priceCurrency": "GBP"
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Course",
                "name": "StudyStyle™ — Cognitive Profiling Assessment",
                "description": "Evaluates learners across five domains including sensory modality, metacognition, motivational dispositions and cognitive styles.",
                "provider": { "@type": "Organization", "name": "Oxbridge Gateway" },
                "educationalLevel": "Secondary, Undergraduate",
                "audience": { "@type": "EducationalAudience", "educationalRole": "student" }
              },
              "price": "0",
              "priceCurrency": "GBP"
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Course",
                "name": "FutureReady™ — Career Alignment Assessment",
                "description": "Profiles students across personality traits, emotional intelligence, vocational interests and work values.",
                "provider": { "@type": "Organization", "name": "Oxbridge Gateway" },
                "educationalLevel": "Secondary, Undergraduate",
                "audience": { "@type": "EducationalAudience", "educationalRole": "student" }
              },
              "price": "0",
              "priceCurrency": "GBP"
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Course",
                "name": "AIReady™ — AI Economy Readiness Assessment",
                "description": "Assesses human capability across four domains using the Oxbridge Gateway AIReady CREATIVE framework.",
                "provider": { "@type": "Organization", "name": "Oxbridge Gateway" },
                "educationalLevel": "Secondary, Undergraduate, Young Professional",
                "audience": { "@type": "EducationalAudience", "educationalRole": "student" }
              },
              "price": "0",
              "priceCurrency": "GBP"
            }
          ]
        }
      },

      /* ── 4. FAQPage ── */
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is ASPIREdeck?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "ASPIREdeck is a free, globally accessible psychometric platform by Oxbridge Gateway. It helps secondary school students, university students and young adults worldwide discover their ideal university subjects, align career choices with personal strengths, and build the human capabilities needed to lead in the AI economy."
            }
          },
          {
            "@type": "Question",
            "name": "Is ASPIREdeck completely free?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. ASPIREdeck is completely free for all users globally. Access is granted via a simple email whitelist. Individuals can request access by emailing product@aspiredeck.com. Schools can request whitelisting of their entire email domain."
            }
          },
          {
            "@type": "Question",
            "name": "Who is ASPIREdeck designed for?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "ASPIREdeck is designed for secondary school students, university students and young adults aged 13 and above worldwide. It is equally valuable for schools seeking to provide evidence-based academic guidance at scale."
            }
          },
          {
            "@type": "Question",
            "name": "What psychometric assessments does ASPIREdeck include?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "ASPIREdeck offers four core psychometric assessments: UniReady™ maps academic interests across 11 disciplinary clusters and 47 subjects; StudyStyle™ profiles cognitive and learning preferences; FutureReady™ aligns career pathways to personality and values; and AIReady™ measures readiness for the AI economy using Oxbridge Gateway's CREATIVE framework."
            }
          },
          {
            "@type": "Question",
            "name": "Who designed ASPIREdeck?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "ASPIREdeck was designed by senior Cambridge and Oxford academics including Dr Lydia Lee (PhD, Cambridge), Dr Gwenllian Williams (DPhil, Oxford), Dr Winnie Pui (PhD, Cambridge), and John Au (MSci, Cambridge). It draws on validated frameworks including the Big Five personality model, Holland vocational interest theory, and cognitive style theory."
            }
          },
          {
            "@type": "Question",
            "name": "How can schools access ASPIREdeck?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Schools can gain access by emailing product@aspiredeck.com with their school domain. Once whitelisted, all users with that school email can create accounts at aspiredeck.com."
            }
          },
          {
            "@type": "Question",
            "name": "Is student data secure on ASPIREdeck?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. All data is securely managed under UK GDPR compliance via Neon, a serverless PostgreSQL provider operating across AWS and Azure cloud infrastructure."
            }
          }
        ]
      },

      /* ── 5. BreadcrumbList ── */
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Oxbridge Gateway",
            "item": "https://www.oxbridgegateway.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "ASPIREdeck",
            "item": "https://www.aspiredeck.com"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": type === "login" ? "Log In" : "Register",
            "item": type === "login"
              ? "https://www.aspiredeck.com/login"
              : "https://www.aspiredeck.com/register"
          }
        ]
      },

      /* ── 6. ItemList — Assessment Modules ── */
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "ASPIREdeck Psychometric Assessment Modules",
        "description": "Four core evidence-based psychometric assessments designed by Cambridge and Oxford experts to guide students aged 13+ in university subject choice and AI-era career planning.",
        "numberOfItems": 4,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "UniReady™ — Academic Profiling",
            "url": "https://www.oxbridgegateway.com/aspiredeck",
            "description": "Three-stage academic interest assessment across 11 disciplinary clusters and 47 university subjects."
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "StudyStyle™ — Cognitive Profiling",
            "url": "https://www.oxbridgegateway.com/aspiredeck",
            "description": "Learning preference and metacognition assessment across 5 domains."
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "FutureReady™ — Career Alignment",
            "url": "https://www.oxbridgegateway.com/aspiredeck",
            "description": "Career assessment combining vocational interests, personality traits and work values."
          },
          {
            "@type": "ListItem",
            "position": 4,
            "name": "AIReady™ — AI Economy Readiness",
            "url": "https://www.oxbridgegateway.com/aspiredeck",
            "description": "Human capability assessment using the Oxbridge Gateway CREATIVE framework."
          }
        ]
      }

    ])}
  </script>

{/* ==================== WHITELIST REQUEST MODAL ==================== */}
{showWhitelistModal && (
  <div className="video-modal-overlay" onClick={() => setShowWhitelistModal(false)}>
    <div className="video-modal-content" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
      <button 
        className="modal-close-btn" 
        onClick={() => setShowWhitelistModal(false)}
      >
        ✕
      </button>

      <h2 style={{ marginBottom: '20px', color: '#36a7f8' }}>Request Whitelist Access</h2>
      <p style={{ marginBottom: '20px', color: '#ccc' }}>
        Please fill in the details below. Our team will review your request shortly.
      </p>

      <form 
        onSubmit={async (e) => {
  e.preventDefault();
  
  // === IMMEDIATE 1 HOUR RESTRICTION ===
  const now = Date.now();
  whitelistState.current.lastAttempt = now;
  saveState();
  updateWhitelistButtonState();

  // Build the final message properly
  let finalMessage = whitelistMessage.trim();

  // If user didn't enter any custom message, use the default one
  if (!finalMessage) {
    finalMessage = `Please whitelist my email address: ${email}\n\nThank you!`;
  } 
  // If user entered something, append the email address for clarity
  else {
    finalMessage += `\n\nMy email address: ${email}`;
  }

  try {
    await axios.post('/api/whitelist/request', {
      fromEmail: email,        // Always send the email field
      message: finalMessage    // Send the final constructed message
    });
    
    alert("Your whitelist request has been sent successfully! We'll review it soon.");
    setShowWhitelistModal(false);
  } catch (err) {
    alert("Failed to send request. Please try again or email us directly at product@aspiredeck.com");
  }
}}
      >
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#aaa' }}>
            Your email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.school@email.com"
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #444',
              background: '#222',
              color: '#fff'
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#aaa' }}>
            To
          </label>
          <input
            type="text"
            value="product@aspiredeck.com"
            readOnly
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #444',
              background: '#1a1a1a',
              color: '#888'
            }}
          />
        </div>

       <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#aaa' }}>
            Message
          </label>
          <textarea
            value={whitelistMessage}
            onChange={(e) => setWhitelistMessage(e.target.value)}
            placeholder="Add any additional information here (school name, reason for access, etc.)"
            rows={6}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #444',
              background: '#222',
              color: '#ddd',
              resize: 'vertical'
            }}
          />
        </div>


        <button 
          type="submit"
          disabled={whitelistButtonDisabled}
          style={{
            width: '100%',
            padding: '14px',
            background: whitelistButtonDisabled ? '#555' : '#36a7f8',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            cursor: whitelistButtonDisabled ? 'not-allowed' : 'pointer',
            opacity: whitelistButtonDisabled ? 0.7 : 1
          }}
        >
          {whitelistButtonText}
        </button>
      </form>
    </div>
  </div>
)}

{/* ==================== QUICK CAREER ASSESSMENT MODAL (Enforced Answer + Fun Design) ==================== */}
{showCareerAssessmentModal && (
  <div className="career-assessment-modal" onClick={() => setShowCareerAssessmentModal(false)}>

    <div 
      className="modal-content quick-modal-content" 
      onClick={e => e.stopPropagation()}   // ← This prevents closing when clicking inside
    >
      <button 
        className="modal-close-btn" 
        onClick={() => setShowCareerAssessmentModal(false)}
      >
        ✕
      </button>

      {!isSubmitted ? (
        <div className="quick-container">
          {/* Header */}
          <div className="quick-header">
            <h2>What's Your Superpower?</h2>
            <p>Discover your ideal career type</p>
            
            <div className="quick-progress-container">
              <div 
                className="quick-progress-bar" 
                style={{ width: `${((currentQuestion - 1) / 10) * 100}%` }}
              />
            </div>
            <div className="quick-progress-text">
              Question {currentQuestion} of 10
            </div>
          </div>

          {/* Carousel */}
          <div className="quick-carousel-wrapper">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((qId) => {
              const question = getQuestion(qId);
              const selected = answers[qId];

              return (
                <div 
                  key={qId}
                  className={`quick-slide ${qId === currentQuestion ? 'active' : ''}`}
                >
                  <div className="quick-question-card">
                    <h3>{question.text}</h3>
                    
                    <div className="quick-options-grid">
                      {question.options.map((opt) => (
                        <label 
                          key={opt.letter}
                          className={`quick-option ${selected === opt.letter ? 'selected' : ''}`}
                          onClick={() => handleAnswer(qId, opt.letter)}
                        >
                          <span className="quick-option-letter">{opt.letter}</span>
                          <span className="quick-option-text">{opt.text}</span>
                        </label>
                      ))}
                    </div>

                    {!selected && (
                      <p className="quick-answer-warning">
                        Please choose an answer to continue
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Side Arrows */}
          <button 
            className="quick-arrow quick-arrow-left"
            onClick={() => goToQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 1}
          >
            ←
          </button>

          <button 
            className="quick-arrow quick-arrow-right"
            onClick={() => {
              if (!isCurrentQuestionAnswered()) {
                alert("Please answer the current question before moving forward.");
                return;
              }
              if (currentQuestion < 10) {
                goToQuestion(currentQuestion + 1);
              } else {
                submitAssessment();
              }
            }}
            disabled={!isCurrentQuestionAnswered()}
          >
            →
          </button>
        </div>
      ) : (
        /* ==================== RESULTS SCREEN (Fixed - No longer dimmed) ==================== */
        <div className="quick-results-screen" onClick={e => e.stopPropagation()}>
          <h2>Your Superpower is...</h2>
          
          <div className="quick-result-badge">
            {result.primary_type}
          </div>

          <p className="quick-description">
            {result.description}
          </p>

          {result.careerTypes && (
            <div className="quick-career-types">
            Possible Careers:<br />
                <strong>{result.careerTypes}</strong>
            </div>
          )}

     
             
               <p className="join-aspiredeck-subtitle">Join 
                  <button 
                 className="join-aspiredeck-btn"
                 onClick={() => setShowWhitelistModal(true)}
               >
                 ASPIREdeck
               </button>
                to access deeper psychometric assessments and empower yourself to shape the future and help drive 10X progress for humanity.
               </p>

        </div>
      )}
    </div>
  </div>
)}

{/* ==================== MODERN CONFETTI CELEBRATION OVERLAY ==================== */}
{showCelebration && (
  <div className="celebration-overlay">
    <div className="celebration-content">
    </div>
  </div>
)}
    </div>
  </>
);
}